// Ported from microsites/womens-conference/src/components/ChoicePreview.tsx verbatim.
import { useState } from 'react';
import type { TopicView } from '../lib/views';
import { Avatar } from './Avatar';
import { LunchThumb } from './Lunch';
import { useMeals } from './MealsProvider';

/**
 * THE DECISIONS SHE HAS NOT EARNED YET
 * ====================================
 *
 * A woman who is signed in but has no seat used to get a sentence explaining
 * that her ticket would appear here one day. That is a worse pitch than the
 * thing itself: the breakouts are the reason to come, and the profile sheet
 * was the one place on the site that refused to show them to her.
 *
 * So the rows render either way, and they render as themselves rather than as
 * disabled stubs — the football microsite's GhostForm move. The real row is
 * blurred back, a padlock sits over the middle with the reason, and the whole
 * thing is one click target pointed at the action that would unlock it. Nothing
 * is greyed out, nothing says "unavailable", and the shape of what she is
 * missing is legible through the blur.
 *
 * Two different locks, on purpose, because they are two different situations:
 *
 *  - NO SEAT. She cannot choose because she has not registered. The preview is
 *    a tease and should read as one: every speaker's face, blurred, and a click
 *    goes to registration.
 *  - CHOICES CLOSED. She has a seat and an answer, and the date to change it
 *    has passed. Her answer is information she still needs, so it is NOT
 *    blurred — it just stops being a button, and says when it locked and why.
 *    Blurring a fact she already owns would be punishing her for being late.
 */

function Padlock({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/**
 * The faces of every topic on offer, overlapped.
 *
 * Overlapped rather than in a row because the point is "six of these", not
 * "these six": she reads a quantity and a hint of who, then the blur takes the
 * rest. Capped with a +n, which is what keeps it the width of one
 * avatar-and-a-bit instead of the width of the sheet — except when the cap
 * would hide exactly one more, in which case the face is drawn instead. See
 * `withOverflow`.
 */
/**
 * How many to draw before a "+n" earns its place.
 *
 * A +n bubble is exactly one circle wide, so "+1" spends a circle to say there
 * is one more — the same room the missing face would have taken, to show a
 * number instead of a person. It only pays for itself from two.
 */
function withOverflow<T>(items: T[], cap: number): { shown: T[]; rest: number } {
  const rest = items.length - cap;
  if (rest === 1) return { shown: items.slice(0, cap + 1), rest: 0 };
  return { shown: items.slice(0, cap), rest: Math.max(rest, 0) };
}

function TopicFaces({ topics, size = 40 }: { topics: TopicView[]; size?: number }) {
  const { shown, rest } = withOverflow(topics, 5);
  return (
    <span className="flex shrink-0 items-center">
      {shown.map((topic, i) => (
        <span
          key={topic.slug}
          className="ring-wm-petal relative inline-flex rounded-full ring-2"
          style={{ marginLeft: i === 0 ? 0 : '-0.7rem', zIndex: shown.length - i }}
        >
          <Avatar
            name={topic.speaker.name}
            imageGuid={topic.speaker.imageGuid}
            imageSrc={topic.speaker.imageSrc}
            size={size}
          />
        </span>
      ))}
      {rest > 0 ? (
        <span
          className="bg-wm-sage-200 text-wm-sage-700 ring-wm-petal relative flex items-center justify-center rounded-full text-xs font-semibold ring-2"
          style={{ marginLeft: '-0.7rem', width: size, height: size }}
        >
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

/** The same idea for lunch: the food, squared off because a plate is not a person. */
function LunchThumbs({ size = 40 }: { size?: number }) {
  const meals = useMeals();
  const { shown: options, rest } = withOverflow(meals.options, 4);
  return (
    <span className="flex shrink-0 items-center">
      {options.map((option, i) => (
        <span
          key={option.value}
          className="ring-wm-petal relative inline-flex rounded-xl ring-2"
          style={{ marginLeft: i === 0 ? 0 : '-0.7rem', zIndex: options.length - i }}
        >
          <LunchThumb option={option} size={size} />
        </span>
      ))}
      {rest > 0 ? (
        <span
          className="bg-wm-sage-200 text-wm-sage-700 ring-wm-petal relative flex items-center justify-center rounded-xl text-xs font-semibold ring-2"
          style={{ marginLeft: '-0.7rem', width: size, height: size }}
        >
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

/**
 * One decision she cannot make yet, shown rather than described.
 *
 * `onEngage` is what the lock is standing in front of: registration when she
 * has no seat, nothing at all when the date has simply passed. A lock with
 * nowhere to go is not a button, which is the same rule the unlocked `Row`
 * follows.
 */
export function LockedChoiceRow({
  label,
  preview,
  media,
  lock,
  onEngage,
}: {
  label: string;
  /** What stands in for her answer: "Six topics to choose from". */
  preview: string;
  media: React.ReactNode;
  /** The padlock's sentence. Short: it sits over the row, not under it. */
  lock: string;
  onEngage?: () => void;
}) {
  const [hot, setHot] = useState(false);
  const Tag = onEngage ? 'button' : 'div';

  return (
    <Tag
      {...(onEngage
        ? { type: 'button' as const, onClick: onEngage, 'aria-label': `${label}. ${lock}` }
        : {})}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      className={`wm-dotted-rule relative -mx-3 block w-full rounded-2xl bg-bottom bg-repeat-x px-3 py-4 text-left transition-colors last:bg-none ${
        onEngage ? 'hover:bg-wm-cream/60' : ''
      }`}
    >
      {/* The row itself, dimmed and softened. One ghost: the label goes with
          it, because a crisp heading over a fuzzy body reads as a rendering
          bug rather than a locked control. */}
      <span
        aria-hidden
        className={`flex items-center gap-3 blur-[2px] transition-opacity duration-300 select-none ${
          hot ? 'opacity-20' : 'opacity-45'
        }`}
      >
        {media}
        <span className="min-w-0">
          <span className="wm-kicker block">{label}</span>
          <span className="text-wm-sage-950 mt-0.5 block text-[0.95rem] font-semibold">
            {preview}
          </span>
        </span>
      </span>

      <span
        aria-hidden
        className="text-wm-sage-700 pointer-events-none absolute inset-0 flex items-center justify-center gap-2"
      >
        <Padlock className="h-4 w-4" />
        <span className="text-xs font-semibold">{lock}</span>
      </span>
    </Tag>
  );
}

export { TopicFaces, LunchThumbs };
